import { motion } from 'motion/react';
import { COMPARISON_TABLE, CX2_EXPEDITION_SPECS, ACCESSORIES_LIST } from '../constants';
import { Table, Check, X, Anchor, Shield, Zap, Package, Activity, Terminal, ShoppingBag } from 'lucide-react';

export default function Specs() {
  return (
    <section className="py-32 bg-[#E4E3E0] relative overflow-hidden" id="c2-specs">
      {/* Structural Grid */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" 
        style={{ backgroundImage: 'linear-gradient(#141414 1px, transparent 1px), linear-gradient(90deg, #141414 1px, transparent 1px)', backgroundSize: '100px 100px' }} 
      />

      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 border-b border-[#141414]/10 pb-8 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Terminal className="h-4 w-4 text-ows-teal" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-[0.5em] text-ows-teal">VALIDATION: BENCHMARK DATA</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-light uppercase tracking-tighter leading-none">
              TECHNICAL <br />
              <span className="text-[#141414]/40">COMPARISON</span>
            </h2>
          </div>
          <div className="text-right">
            <span className="text-[9px] font-mono font-bold uppercase tracking-widest bg-white px-2 py-1 border border-[#141414]/10 rounded shadow-sm">REFERENCE: STANDARD EXPEDITION SUP</span>
          </div>
        </div>

        {/* Comparison Table: The High-Authority Matrix */}
        <div className="overflow-x-auto mb-20 bg-white border border-[#141414]/10 rounded-2xl shadow-2xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#141414] text-white">
                <th className="py-6 px-8 text-[10px] font-mono font-bold tracking-[0.3em] uppercase opacity-40">METRIC</th>
                <th className="py-6 px-8 text-xs font-mono font-bold tracking-[0.3em] uppercase text-ows-teal flex items-center gap-2">
                  <Shield className="h-3 w-3" /> OWS EXPEDITION CX2
                </th>
                <th className="py-6 px-8 text-[10px] font-mono font-bold tracking-[0.3em] uppercase opacity-40">MARKET STANDARD</th>
              </tr>
            </thead>
            <tbody className="text-[#141414]">
              {COMPARISON_TABLE.map((row) => (
                <tr key={row.feature} className="border-b border-[#141414]/5 hover:bg-[#E4E3E0]/30 transition-colors group">
                  <td className="py-6 px-8">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-[#141414]/60">{row.feature}</span>
                  </td>
                  <td className="py-6 px-8">
                    <span className="text-xs font-mono font-bold text-ows-teal uppercase tracking-widest">{row.ows}</span>
                  </td>
                  <td className="py-6 px-8">
                    <span className="text-[10px] font-mono font-bold text-[#141414]/30 uppercase tracking-widest">{row.standard}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detailed Specs Grid: The Industrial Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {Object.entries(CX2_EXPEDITION_SPECS).map(([key, value], i) => (
            <motion.div 
              key={key}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white/60 backdrop-blur-sm p-6 border border-[#141414]/10 rounded-2xl group hover:border-ows-teal/30 hover:-translate-y-1 transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-3 w-3 text-ows-orange opacity-40 group-hover:opacity-100 transition-opacity" />
                <span className="text-[9px] font-mono font-bold tracking-widest uppercase text-[#141414]/40">{key}</span>
              </div>
              <span className="text-lg font-mono font-bold text-[#141414] tracking-tighter uppercase">{value}</span>
            </motion.div>
          ))}
        </div>

        {/* Accessories Inclusion */}
        <div className="bg-white border border-[#141414]/10 rounded-3xl p-10 shadow-lg">
           <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-ows-teal/10 rounded-xl">
                 <ShoppingBag className="h-5 w-5 text-ows-teal" />
              </div>
              <div>
                 <h3 className="text-xs font-mono font-bold uppercase tracking-[0.4em]">Integrated Deployment Kit</h3>
                 <p className="text-[10px] font-mono text-[#141414]/40 uppercase tracking-widest">Invoiced as Standard Inclusion for Batch 01</p>
              </div>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ACCESSORIES_LIST.map((acc, i) => (
                <div key={i} className="flex items-center gap-3">
                   <Check className="h-3 w-3 text-ows-teal" />
                   <span className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/60">{acc}</span>
                </div>
              ))}
           </div>
        </div>

        <div className="mt-20 pt-12 border-t border-[#141414]/10 flex flex-col md:flex-row items-center justify-between gap-8 opacity-40">
           <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3" />
                <span className="text-[8px] font-mono font-bold uppercase tracking-widest">Hydraulic Verified</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3" />
                <span className="text-[8px] font-mono font-bold uppercase tracking-widest">Weight-Calibrated</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3 w-3" />
                <span className="text-[8px] font-mono font-bold uppercase tracking-widest">Batch-Validated</span>
              </div>
           </div>
           <p className="text-[8px] font-mono font-bold uppercase tracking-[0.4em]">Proprietary Data: Open Water Syndicate Labs</p>
        </div>
      </div>
    </section>
  );
}
